import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import { AxiosResponse, AxiosError } from 'axios';
import MainLayout from '../../components/MainLayout';
import StatusMessages, { StatusMessage } from '../../components/StatusMessages';
import { resendActivationEmail } from '../../api/accounts';
import styles from './SignUpRedirect.module.css';
import { NOT_FOUND_ROUTE } from '../../config/constants';

export interface SignUpRedirectProps {
  email?: string
  // status messages to display on top
  // can be passed through props or through state
  statusMessages?: StatusMessage[]
};

// where users are redirected after signing up
// allows resending of activation email
const SignUpRedirect = (props: SignUpRedirectProps): JSX.Element | null => {
  // get state passed by previous location
  const { state } = useLocation();
  // status messages to display on top
  const [statusMessages, setStatusMessages] = useState<StatusMessage[]>(
    // get status messages from props
    // if undefined, get status messages from state passed by previous location
    // otherwise leave empty
    props.statusMessages ?? state?.statusMessages ?? []
  );
  // resend activation email call enabled/disabled state
  const [resendDisabled, setResendDisabled] = useState<boolean>(false);
  const navigate = useNavigate();

  // email to resend activation instructions to
  const email = props?.email ?? state?.email;

  // set document title
  useEffect(() => {
    document.title = 'Sign Up | Sigmoid Academy';
  }, []);

  useEffect(() => {
    if (!email) {
      navigate(NOT_FOUND_ROUTE);
    }
  }, [email, navigate]);

  // resend activation email
  const handleResendActivationEmail = () => {
    if (resendDisabled) {
      return;
    }

    // skip if email not provided
    if (!email) {
      return;
    }

    // clear status messages
    setStatusMessages((previousMessages: StatusMessage[]) => []);
    setResendDisabled(true);

    // resend activation email
    resendActivationEmail(
      email
    ).then((response: AxiosResponse) => setStatusMessages(
        // update status messages with success message on success
        (previousMessages: StatusMessage[]) => [
          {
            message: 'Resent activation email',
            status: 'success'
          }
        ]
      )
    ).catch((error: AxiosError) => setStatusMessages(
        // update status messages with danger message on failure
        (previousMessages: StatusMessage[]) => [
          {
            message: 'Unknown error occurred',
            status: 'error'
          }
        ]
      )
    ).finally(() => setResendDisabled(false));
  };

  return (
    <div>
      <MainLayout>
        <div className={styles.mainContainer}>
          <StatusMessages items={statusMessages} />
          <Button
            type='primary'
            className='mainButton'
            onClick={handleResendActivationEmail}
            loading={resendDisabled}
          >
            Resend Activation Email
          </Button>
        </div>
      </MainLayout>
    </div>
  );
};

export default SignUpRedirect;